import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { map, Observable, Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { DialogDeleteDraftComponent,
	RESPONSE_CONFIRM } from '../../../shared/components/dialog-delete-draft/dialog-delete-draft.component';
import {
	SnackbarDeleteDraftComponent
} from '../../../shared/components/snackbar-delete-draft/snackbar-delete-draft.component';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { TranslationService } from '../../../shared/services/translation.service';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { OrderDraft } from '../../models/eorder';
import { EorderApiService } from '../../services/eorder.api.service';
import { EorderWizardService } from '../../services/eorder-wizard.service';
import { LocationService } from '../../../shared/services/location.service';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';

@Component({
	selector: 'copo-order-draft-list',
	templateUrl: './order-draft-list.component.html',
	styleUrl: './order-draft-list.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		PageHeaderComponent,
		NgIf,
		VirtualScrollerDirective,
		NgFor,
		MatIconModule,
		TranslateModule,
		MatRippleModule,
		MatTooltipModule,
		MatButtonModule,
		LoadingSpinnerComponent,
		DatePipe
	]
})
export class OrderDraftListComponent implements OnDestroy {
	@HostBinding('class.copo-order-tracking-block') readonly block = true;
	readonly UNDERSCORE = UNDERSCORE;
	isLoading = false;
	orderDraft?: OrderDraft;
	requiredFields: (keyof OrderDraft)[] = [
		'orderID',
		'customerOrderNumber',
		'creationDate',
		'creatorName',
		'creatorID',
		'modificationDate',
	];

	filters = '';
	sortWay = OrderByDirection.DESC;
	sortBy: keyof OrderDraft = 'modificationDate';
	deleteDraftSubscription?: Subscription;

	constructor(
		private readonly eorderApiService: EorderApiService,
		private readonly router: Router,
		private readonly translationService: TranslationService,

		private readonly dialog: MatDialog,

		private readonly snackBar: MatSnackBar,
		private readonly eorderWizardService: EorderWizardService,
		private readonly locationService: LocationService
	) {}

	get dataId() {
		return `order-draft-list-${ this.sortWay
		}-${ this.sortBy
		}`;
	}

	ngOnDestroy() {
		if (this.deleteDraftSubscription) {
			this.deleteDraftSubscription.unsubscribe();
		}
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<OrderDraft[]>) => void) {
		provideData(
			this.eorderApiService.getDraftOrders(
				this.requiredFields,
				this.filters,
				index,
				limit,
				new OrderBy(this.sortBy, this.sortWay),
				false
			).pipe(map(res => res.results.map((order: OrderDraft) => {
				this.orderDraft = order;
				return order;
			})))
		);
	}

	getOrderDraftName(order: OrderDraft) {
		if (order.customerOrderNumber) {
			return `${ this.translationService.getTR('draft.value') }
			${ order.customerOrderNumber }`;
		}
		return this.translationService.getTR('draft.value');
	}

	getNoOfProducts(order: OrderDraft) {
		return order.noOfProducts;
	}

	getProductFamilies(order: OrderDraft) {
		const result: { subCategoryDescription: string; formDescription?: string }[] = [];
		if (order.listOfProductFamily) {
			for (const productFamily of order.listOfProductFamily) {
				if (productFamily.subCategoryDescription) {
					result.push({
						subCategoryDescription: productFamily.subCategoryDescription,
						formDescription: productFamily.formDescription || ''
					});
				}
			}
		}
		return result.length > 0 ? result : [{ subCategoryDescription: UNDERSCORE }];
	}

	getTotalQuantity(order: OrderDraft) {
		return order.totalQuantity;
	}

	getCreatorName(order: OrderDraft) {
		return order.creatorName;
	}

	getCreatorID(order: OrderDraft) {
		return order.creatorID;
	}

	async evtEditEordrDraft(order: OrderDraft) {
		if (order.orderID) {
			this.isLoading = true;
			await this.eorderWizardService.actionEditEordrDraft(order.orderID);
			this.isLoading = false;
		}
	}

	evtDeleteDraftOrder(order: OrderDraft) {
		const path = '/order/draft';
		const sub = this.dialog.open<DialogDeleteDraftComponent, number>(
			DialogDeleteDraftComponent,
			{ panelClass: 'copo-offer-dialog' }
		);
		sub.componentInstance.draftName = order.customerOrderNumber?.toString();
		this.deleteDraftSubscription = sub.afterClosed()
			.subscribe(async response => {
				if (response === RESPONSE_CONFIRM && order.orderID) {
					try {
						await this.eorderApiService.deleteDraftOrders(order.orderID).then(async () => {
							this.snackBar.openFromComponent(
								SnackbarDeleteDraftComponent,
								{
									data: this.translationService.getTR('eorder.draft.deleted.successfully'),
									duration: 5000,
									panelClass: ['copo-draft-snackbar']
								}
							);
							await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
						});
					} catch (err) {
						console.error('unable to delete draft', err);
						this.snackBar.open(
							this.translationService.getTR(
								'error.deleting.draft'
							),
							undefined,
							{
								duration: 5000,
							}
						);
						await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
					}
				}
			});
	}

	async evtGoToOrderCard(order: OrderDraft) {
		if (order.orderID) {
			this.isLoading = true;
			await this.eorderWizardService.viewEordrDraft(order.orderID);
		}
	}

	goBack() {
		this.locationService.back();
	}
}
